import { useMemo } from "react";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useCODTrustScores } from "@/hooks/admin/usePayments";
import type { CODTrustScore } from "@/lib/admin-api/payment";
import { formatDate, scoreColor } from "./formatters";

export function CODTrustTab() {
  const { data, isLoading, isError, refetch } = useCODTrustScores();
  const scores = data?.scores ?? [];

  const columns: readonly ColumnDef<CODTrustScore>[] = useMemo(
    () => [
      {
        id: "agent_name",
        header: "Agent",
        cell: (s) => (
          <span className="font-medium text-stone-900">{s.agent_name}</span>
        ),
      },
      {
        id: "score",
        header: "Score",
        cell: (s) => (
          <span
            className={`rounded-full px-3 py-0.5 text-sm font-semibold ${scoreColor(s.score)}`}
          >
            {s.score} / 100
          </span>
        ),
      },
      {
        id: "collections_count",
        header: "Collectes",
        cell: (s) => (
          <span className="text-sm text-stone-700">{s.collections_count}</span>
        ),
      },
      {
        id: "discrepancy_rate",
        header: "Taux d'écart",
        cell: (s) => (
          <span
            className={`text-sm font-medium ${s.discrepancy_rate > 0.05 ? "text-red-600" : "text-stone-700"}`}
          >
            {((s.discrepancy_rate ?? 0) * 100).toLocaleString("fr-SN", {
              maximumFractionDigits: 1,
            })}
            %
          </span>
        ),
      },
      {
        id: "last_flag_at",
        header: "Dernière anomalie",
        cell: (s) => (
          <span className="text-sm text-stone-500">
            {s.last_flag_at ? formatDate(s.last_flag_at) : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Impossible de charger les scores de confiance.</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-600">
        <ShieldCheck className="h-4 w-4 text-amber-600" />
        <span>
          Le score de confiance COD est calculé à partir de l'historique des
          collectes et des écarts de caisse de chaque agent.
        </span>
      </div>

      {!isLoading && scores.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="h-6 w-6" />}
          title="Aucun score disponible"
          description="Les scores de confiance apparaîtront ici après les premières collectes COD."
        />
      ) : (
        <AdminDataTable data={scores} columns={columns} isLoading={isLoading} />
      )}
    </div>
  );
}
